import React, { useContext, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { Link as RouterLink } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { StationContext } from '../context/StationContext';
import {
  CALENDAR_PAGE_PERMISSIONS,
  CONFIGURATION_PERMISSIONS,
  hasAnyRequiredPermission,
  PERSONAL_PAGE_PERMISSIONS,
  REPORT_PERMISSIONS,
} from '../../config/routePermissions';

const GETTING_STARTED_STEPS = [
  'Seleccioná la estación.',
  'Verificá la configuración de la estación: sus turnos y si rota.',
  'Verificá que estén todos los empleados ingresados y bien configurados, con sus restricciones y su fecha de ingreso.',
  'Creá el calendario.',
  'Revisá coberturas y alertas.',
  'Modificá manualmente lo que haga falta.',
];

const GETTING_STARTED_TIPS = [
  'Imprimí tu calendario para compartirlo.',
  'Cuando termina el mes, cerrá el calendario: esto lo congela y permite obtener reportes de horas.',
];

const WARNING_GROUPS = [
  {
    group: 'Sobre un turno o una franja horaria',
    items: [
      {
        title: 'Turnos del día',
        summary: 'Un turno puntual quedó con más o menos personas de las esperadas.',
        details: [
          'Compara la dotación real de ese turno contra la cantidad configurada para él (la "cobertura" que se define por estación, cruzando turnos con áreas).',
          'Ejemplo: el turno Mañana de Caja necesita 2 personas y ese día quedó con 1.',
        ],
      },
      {
        title: 'Cobertura del día',
        summary: 'Queda una franja horaria del día sin ningún turno que la cubra.',
        details: [
          'A diferencia de "Turnos del día", esto no es un turno mal dotado: es un hueco de horario entre turnos, aunque cada turno individual esté completo.',
          'Ejemplo: hay turnos que cubren de 8 a 14 y de 18 a 22, pero nadie cubre de 14 a 18.',
        ],
      },
    ],
  },
  {
    group: 'Sobre una persona',
    items: [
      {
        title: 'Advertencias por empleado y día',
        summary: 'Inconsistencias concretas de una persona en una fecha puntual.',
        details: [
          'Ejemplos: figura trabajado y libre a la vez, trabajado y con licencia, un feriado mal marcado, o un turno que ya no existe.',
          'También avisa si una persona inactiva sigue asignada, o si tiene un turno nocturno sin tener habilitada la nocturnidad.',
        ],
      },
      {
        title: 'Rotación de quincena',
        summary: 'El turno asignado al abrir la quincena no siguió la rotación esperada.',
        details: [
          'Aparece si la persona mantiene el mismo turno cuando el sistema esperaba un cambio.',
          'También si el nuevo turno deja muy poco descanso respecto al de la quincena anterior.',
        ],
      },
      {
        title: 'Descanso entre jornadas',
        summary: 'El tiempo libre entre el fin de un turno y el inicio del siguiente es insuficiente.',
        details: [
          'Si los turnos se superponen (empieza uno antes de que termine el anterior), es una inconsistencia crítica.',
          'Si no se superponen pero el descanso queda por debajo del mínimo configurado, se muestra como advertencia.',
        ],
      },
      {
        title: 'Patrón de días libres',
        summary: 'La secuencia de trabajo y descanso de la persona no coincide con lo esperado.',
        details: [
          'Puede señalar que debería trabajar y figura libre, o que debería estar libre y quedó asignada a trabajar.',
        ],
      },
      {
        title: 'Advertencias del empleado en el período',
        summary: 'Observación de una persona que no corresponde a un día puntual sino a toda la quincena.',
        details: [
          'Es la menos frecuente de este grupo: aparece cuando algo de esa persona no encaja en ninguna de las categorías anteriores.',
        ],
      },
    ],
  },
  {
    group: 'Sin categoría específica',
    items: [
      {
        title: 'Advertencias generales del día',
        summary: 'Avisos de un día puntual que no encajan en "Turnos del día" ni en "Cobertura del día".',
        details: [
          'Funciona como respaldo para ese día, mientras se termina de afinar a qué categoría específica pertenece.',
        ],
      },
      {
        title: 'Advertencias generales',
        summary: 'Mensajes que no corresponden a ningún día ni persona en particular.',
        details: [
          'Sirve para mantener visibles validaciones nuevas o más globales mientras se afinan sus clasificaciones.',
        ],
      },
    ],
  },
];

const FAQ_GROUPS = [
  {
    group: 'Turnos',
    icon: <AccessTimeIcon color="primary" />,
    items: [
      {
        question: '¿Qué diferencia hay entre un turno global y uno local?',
        answer: [
          'Global: está disponible para todas las estaciones. Se crea una sola vez y cualquier estación lo puede usar.',
          'Local: es propio de una sola estación. Solo esa estación lo ve y lo puede asignar.',
        ],
      },
      {
        question: '¿Qué es un turno "con descanso" (turno cortado)?',
        answer:
          'Es un turno que incluye una pausa intermedia entre la entrada y la salida (por ejemplo: entra a las 8, sale a las 12, retoma a las 14 y sale a las 18). El descanso siempre queda dentro del horario del turno.',
      },
      {
        question: '¿Qué es la cobertura y cómo impacta en el calendario?',
        answer: [
          'La cobertura es la cantidad de personas esperada para cada turno. Se configura por estación, cruzando turnos con áreas.',
          'El calendario compara la dotación real asignada contra esa cantidad esperada: si un turno queda con más o menos personas de las esperadas aparece el aviso "Turnos del día", y si en algún horario del día no hay cobertura completa aunque haya turnos cargados aparece "Cobertura del día".',
        ],
      },
    ],
  },
  {
    group: 'Asignación de personal',
    icon: <AssignmentIndIcon color="primary" />,
    items: [
      {
        question: '¿Qué modalidades de asignación existen?',
        answer: [
          'Regular: rota según la planificación del equipo.',
          'Horario fijo semanal: mismo horario todas las semanas, definido manualmente.',
          'Rotación restringida: rota solo entre los turnos que elijas para esa persona.',
          'Ocasional: no se le asigna ningún turno automáticamente; se carga manualmente desde el calendario en los días que trabaje.',
        ],
      },
      {
        question: '¿Por qué se cambió sola la modalidad al marcar "Es encargado"?',
        answer:
          'Al marcar a alguien como encargado, el sistema cambia su modalidad a Horario fijo automáticamente, porque un encargado no rota.',
      },
      {
        question: '¿Qué necesito para usar "Rotación restringida"?',
        answer: 'Elegir al menos un turno en "Turnos permitidos para rotar". Sin eso, no se puede guardar la ficha.',
      },
      {
        question: '¿Qué es la "vigencia" del rol auxiliar?',
        answer: 'Es la fecha desde la que un cambio de área o posición auxiliar empieza a aplicarse en los calendarios abiertos.',
      },
      {
        question: '¿Cómo agrego una licencia o una falta a un empleado?',
        answer: [
          'Desde la ficha del empleado: pestaña "Inasistencias" > botón "+". Completás fecha de inicio, fecha de fin y el tipo de inasistencia.',
          'Desde el calendario: click en el día de esa persona > "Agregar Inasistencia". Se abre el mismo formulario con esa fecha ya cargada; si ese día la persona tiene más de un área asignada, se aplica a todas.',
          'Si el rango elegido se cruza con un calendario abierto, el sistema pide confirmar antes de guardar; si se cruza con uno ya cerrado, no deja guardar esa parte.',
        ],
      },
    ],
  },
  {
    group: 'Calendario',
    icon: <CalendarTodayIcon color="primary" />,
    items: [
      {
        question: '¿Cómo rota el turno de una persona entre quincenas?',
        answer: [
          'Que un área rote o no se define en Configuración > Estaciones: cada estación tiene un switch "Rotar turnos por defecto", y cada área puede sobrescribirlo (Rota / Fijo).',
          'En un área que rota, al abrir una nueva quincena una persona en modalidad Regular recibe un turno base entre los turnos "principales" de su área/posición. Puede repetir el de la quincena anterior o cambiar.',
          'Un día puntual dentro de la quincena igual se puede cambiar a otro turno sin afectar el resto del período.',
          'Los encargados con horario fijo no rotan: trabajan siempre el mismo horario y descansan los domingos.',
        ],
      },
      {
        question: '¿Cómo se crea un calendario?',
        answer:
          'Con el botón "Crear calendario", que aparece cuando el sistema no encuentra uno para esa estación, área/posición y rango de fechas. Al crearlo, el sistema avanza automáticamente al período siguiente.',
      },
      {
        question: '¿Qué implica cerrar un calendario?',
        answer: [
          'Bloquea la edición de turnos, licencias y días libres de ese período.',
          'El contenido queda visible e imprimible, como referencia para auditoría: no se borra nada.',
          'Si quedan calendarios abiertos de hace más de un mes en la misma estación, hay que cerrarlos antes de poder crear uno nuevo.',
          'Para confirmar el cierre hay que escribir la palabra "cerrar". Después, se pueden generar e imprimir reportes de ese período.',
        ],
      },
    ],
  },
];

const ALERT_GUIDE = [
  {
    title: 'Alertas o errores críticos',
    summary: 'Representan inconsistencias más fuertes que normalmente requieren corrección manual.',
    details: [
      'Un ejemplo actual es el control de encargado: un día sin encargado asignado o con más de un encargado.',
      'La recomendación es revisar primero estas alertas antes de interpretar el resto de las advertencias.',
    ],
  },
];

const SUPPORT_EMAIL = 'ayuda.commit@gmail.com';

// Único estilo de contenedor para todas las secciones de la página: un borde
// suave y el mismo radio, sin fondos ni sombras. Da límite visual a cada
// bloque sin volver a la mezcla de Paper/gradientes/colores de antes.
const sectionCardSx = {
  border: '1px solid rgba(0, 35, 57, 0.12)',
  borderRadius: 2,
  p: 3,
};

function HelpPage() {
  const { user, permissions } = useContext(UserContext);
  const { selectedStation, loading } = useContext(StationContext);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      // Clipboard API no disponible (permiso denegado, contexto no seguro, etc.):
      // el botón de mailto sigue funcionando como alternativa.
    }
  };

  const quickActions = useMemo(() => {
    const actions = [];

    if (hasAnyRequiredPermission(permissions, PERSONAL_PAGE_PERMISSIONS)) {
      actions.push({
        title: 'Personal',
        description: 'Consulta el padrón, busca personas y entra a la edición de fichas habilitadas.',
        to: '/personal_table',
        icon: <GroupIcon sx={{ fontSize: 34 }} />,
      });
    }

    if (hasAnyRequiredPermission(permissions, CALENDAR_PAGE_PERMISSIONS)) {
      actions.push({
        title: 'Planificador',
        description: 'Revisa calendarios, turnos y movimientos del período según tu perfil.',
        to: '/planificador',
        icon: <CalendarTodayIcon sx={{ fontSize: 34 }} />,
      });
    }

    if (hasAnyRequiredPermission(permissions, REPORT_PERMISSIONS)) {
      actions.push({
        title: 'Reportes',
        description: 'Genera reportes por estación y período para seguimiento operativo.',
        to: '/reportes',
        icon: <DescriptionIcon sx={{ fontSize: 34 }} />,
      });
    }

    if (hasAnyRequiredPermission(permissions, CONFIGURATION_PERMISSIONS)) {
      actions.push({
        title: 'Configuración',
        description: 'Administra catálogos, usuarios, estaciones y parámetros disponibles para tu acceso.',
        to: '/configuracion',
        icon: <SettingsIcon sx={{ fontSize: 34 }} />,
      });
    }

    return actions;
  }, [permissions]);

  const normalizedRole = user?.role
    ? user.role.replace(/_/g, ' ')
    : 'sin rol asignado';

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Centro de ayuda
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
        <Chip icon={<LockOpenIcon />} label={`Rol: ${normalizedRole}`} variant="outlined" />
        <Chip
          icon={<PlaceIcon />}
          label={`Estación: ${loading ? 'Cargando...' : selectedStation?.name || 'Sin seleccionar'}`}
          variant="outlined"
        />
      </Stack>

      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          mb: 5,
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          bgcolor: 'secondary.light',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MailOutlineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography fontWeight={700} sx={{ color: 'primary.main' }}>
              ¿Tenés dudas, algo no funciona o no se entiende?
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0, 35, 57, 0.75)' }}>
              Escribinos y te ayudamos.
            </Typography>
          </Box>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.main',
            borderRadius: 2,
            pl: 2,
            pr: 0.75,
            py: 0.75,
          }}
        >
          <MailOutlineIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
          <Typography
            component="a"
            href={`mailto:${SUPPORT_EMAIL}`}
            sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {SUPPORT_EMAIL}
          </Typography>
          <Tooltip title={emailCopied ? '¡Copiado!' : 'Copiar dirección'}>
            <IconButton
              onClick={handleCopyEmail}
              size="small"
              sx={{
                color: '#FFFFFF',
                bgcolor: 'rgba(255, 255, 255, 0.14)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.24)' },
              }}
            >
              {emailCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4, alignItems: 'stretch' }}>
        <Grid item xs={12} lg={8}>
          <Box sx={{ ...sectionCardSx, height: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Accesos rápidos
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Te llevan directo a los módulos disponibles para tu usuario.
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action) => (
                <Grid item xs={12} sm={6} key={action.to}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                    <CardActionArea
                      component={RouterLink}
                      to={action.to}
                      sx={{ height: '100%', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'secondary.light',
                            color: 'primary.main',
                          }}
                        >
                          {action.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          {action.title}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {action.description}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
                          <Typography color="primary.main" fontWeight={700} variant="body2">
                            Abrir módulo
                          </Typography>
                          <ArrowForwardIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {quickActions.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Tu usuario no tiene módulos operativos visibles en este momento. Si esperabas ver alguno,
                conviene revisar permisos o rol asignado.
              </Alert>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ ...sectionCardSx, height: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Primeros pasos
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              El flujo típico para armar el calendario de una estación.
            </Typography>
            <List disablePadding>
              {GETTING_STARTED_STEPS.map((step, index) => (
                <ListItem key={step} disableGutters sx={{ alignItems: 'flex-start', py: 0.75 }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: '#FFFFFF',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText primary={step} primaryTypographyProps={{ color: 'text.secondary' }} />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <List disablePadding>
              {GETTING_STARTED_TIPS.map((tip) => (
                <ListItem key={tip} disableGutters sx={{ alignItems: 'flex-start', py: 0.75 }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                    <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={tip} primaryTypographyProps={{ color: 'text.secondary' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ ...sectionCardSx, mb: 4 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Preguntas frecuentes
      </Typography>
      <Stack spacing={3.5}>
        {FAQ_GROUPS.map((group) => (
          <Box key={group.group}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              {group.icon}
              {group.group}
            </Typography>

            <Stack spacing={1.5}>
              {group.items.map((item) => (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(0, 35, 57, 0.12)',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    sx={{ px: 2, py: 0.5 }}
                  >
                    <Typography fontWeight={700}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, py: 1.5 }}>
                    {Array.isArray(item.answer) ? (
                      <List disablePadding>
                        {item.answer.map((line) => (
                          <ListItem key={line} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                              <CheckCircleOutlineIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={line}
                              primaryTypographyProps={{ color: 'text.secondary' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">{item.answer}</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      </Box>

      <Box sx={sectionCardSx}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Advertencias y alertas del planificador
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        Qué significa cada grupo del panel de eventos del planificador y en qué situaciones puede aparecer.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Las advertencias no siempre implican que el calendario sea inválido, pero sí marcan puntos
        que conviene revisar con criterio operativo. Las alertas críticas deberían revisarse primero.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
          >
            <WarningAmberIcon color="warning" />
            Advertencias
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se agrupan según a qué se refieren: un turno o franja horaria, una persona, o sin categoría específica.
          </Typography>

          <Stack spacing={3}>
            {WARNING_GROUPS.map((group) => (
              <Box key={group.group}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                  {group.group}
                </Typography>
                <Stack spacing={1.5}>
                  {group.items.map((item) => (
                    <Accordion
                      key={item.title}
                      disableGutters
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid rgba(0, 35, 57, 0.12)',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon color="warning" />} sx={{ px: 2, py: 0.5 }}>
                        <Box>
                          <Typography fontWeight={700}>{item.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.summary}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2.5, py: 1.5 }}>
                        <List disablePadding>
                          {item.details.map((detail) => (
                            <ListItem key={detail} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                                <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={detail}
                                primaryTypographyProps={{ color: 'text.secondary' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
          >
            <ErrorOutlineIcon color="error" />
            Alertas críticas
          </Typography>

          <Stack spacing={1.5}>
            {ALERT_GUIDE.map((item) => (
              <Box
                key={item.title}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(0, 35, 57, 0.12)',
                }}
              >
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {item.summary}
                </Typography>
                <List disablePadding>
                  {item.details.map((detail) => (
                    <ListItem key={detail} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                        <ErrorOutlineIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={detail}
                        primaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}

            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'secondary.light' }}>
              <Typography fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
                Orden sugerido de revisión
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(0, 35, 57, 0.82)' }}>
                Primero alertas críticas, luego cobertura y turnos del día, después rotación y descansos,
                y por último patrones de libres o inconsistencias puntuales por persona.
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}

export default HelpPage;
